import React from "react";

/* Lightweight <picture> wrapper.
 *
 * Pass a .png or .jpg path and we'll emit:
 *   <picture>
 *     <source type="image/avif" srcSet={src.replace(/\.\w+$/, ".avif")} />
 *     <source type="image/webp" srcSet={src.replace(/\.\w+$/, ".webp")} />
 *     <img src={src} ... />
 *   </picture>
 *
 * Browsers automatically pick the first format they support, so modern
 * browsers download the AVIF (~10-20% the size of PNG) and older
 * browsers fall back to the original. All <img> props are forwarded.
 *
 * Requires the .webp and .avif siblings to already exist on disk –
 * `scripts/generate-modern-images.mjs` runs in the prebuild step and
 * produces them for every image under /public/images/.
 *
 * Usage:
 *   <Picture src="/images/hero/hero-1.jpg" alt="..." loading="lazy" />
 *
 * If `src` doesn't end in a known raster extension we render a plain
 * <img> – safe default for SVGs, data URIs, GIFs, etc.
 */
const RASTER_RE = /\.(png|jpe?g)$/i;

const Picture = ({ src, ...imgProps }) => {
  if (!src || !RASTER_RE.test(src)) {
    return <img src={src} {...imgProps} />;
  }
  const avif = src.replace(RASTER_RE, ".avif");
  const webp = src.replace(RASTER_RE, ".webp");
  return (
    <picture>
      <source type="image/avif" srcSet={avif} />
      <source type="image/webp" srcSet={webp} />
      <img src={src} {...imgProps} />
    </picture>
  );
};

export default Picture;
